import React, { useState, useEffect, useId } from 'react';
import clsx from 'clsx';

interface SwitchProps {
  className?: string;
  label?: string;
  /** Optional id to associate the visual label and the control (helps with a11y and testing) */
  id?: string;
  /** Class name for the label element */
  labelClassName?: string;
  checked?: boolean; // controlled
  defaultChecked?: boolean; // uncontrolled initial
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  children?: React.ReactNode; // rendered only when checked
}

export const Switch: React.FC<SwitchProps> = ({
  className = '',
  label = '',
  id,
  labelClassName,
  checked,
  defaultChecked = false,
  onCheckedChange,
  disabled = false,
  children,
}) => {
  const [internal, setInternal] = useState(defaultChecked);
  const isControlled = typeof checked === 'boolean';
  const isOn = isControlled ? !!checked : internal;
  const autoId = useId();
  const controlId = id ?? `switch-${autoId}`;
  const labelId = `${controlId}-label`;

  useEffect(() => {
    if (isControlled) return;
    setInternal(defaultChecked);
  }, [defaultChecked, isControlled]);

  const toggle = () => {
    if (disabled) return;
    const next = !isOn;
    if (!isControlled) setInternal(next);
    onCheckedChange?.(next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className={clsx('flex w-full flex-col gap-2', className)}>
      <div className="inline-flex w-full items-center justify-between">
        {label && (
          <label
            id={labelId}
            htmlFor={controlId}
            className={clsx(
              'text-button-sm leading-4 font-medium text-neutral-500',
              labelClassName,
            )}
            onClick={(e) => {
              e.preventDefault();
              toggle();
            }}
          >
            {label}
          </label>
        )}
        <button
          id={controlId}
          type="button"
          role="switch"
          aria-checked={isOn}
          aria-labelledby={label ? labelId : undefined}
          disabled={disabled}
          onClick={toggle}
          onKeyDown={onKeyDown}
          className={clsx(
            'relative h-5 w-9 cursor-pointer overflow-hidden rounded-full shadow-inner outline-1 outline-neutral-100 transition-colors',
            isOn ? 'bg-indigo-500' : 'bg-neutral-100',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          data-state={isOn ? 'on' : 'off'}
          data-disabled={disabled ? '' : undefined}
        >
          <span
            className={clsx(
              'bg-neutral-0 absolute top-[2px] h-4 w-4 rounded-full shadow-sm transition-all',
              isOn ? 'left-[18px] ring-1 ring-indigo-500' : 'left-[2px] ring-1 ring-neutral-100',
            )}
          />
        </button>
      </div>

      {isOn && children}
    </div>
  );
};

export default Switch;
